import {
  DestroyRef,
  Injectable,
  PLATFORM_ID,
  computed,
  inject,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, interval } from 'rxjs';
import { AuthService } from './auth.service';
import { NotificationService } from './notification.service';
import {
  getJwtSecondsUntilExpiry,
  isJwtExpiringSoon,
} from '../utils/jwt.util';

/** Exibir aviso quando faltar este tempo (ou menos) para o refresh expirar. */
export const SESSION_RENEWAL_WARNING_SEC = 5 * 60;

export function formatSessionTimeRemaining(seconds: number | null): string {
  if (seconds == null || seconds <= 0) {
    return 'em instantes';
  }
  if (seconds < 60) {
    return 'menos de 1 minuto';
  }
  const mins = Math.ceil(seconds / 60);
  return mins === 1 ? '1 minuto' : `${mins} minutos`;
}

@Injectable({ providedIn: 'root' })
export class SessionRenewalMonitorService {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly notification = inject(NotificationService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  private readonly dismissed = signal(false);
  private readonly tick = signal(0);
  private readonly routeUrl = signal(this.router.url);
  readonly renewing = signal(false);

  readonly visible = computed(() => {
    this.tick();
    this.routeUrl();

    if (!this.isBrowser || this.dismissed() || this.isPublicRoute()) {
      return false;
    }

    const refresh = this.getValidRefreshToken();
    if (!refresh) {
      return false;
    }

    return isJwtExpiringSoon(refresh, SESSION_RENEWAL_WARNING_SEC);
  });

  readonly timeLabel = computed(() => {
    this.tick();
    const refresh = this.getValidRefreshToken();
    if (!refresh) {
      return '';
    }
    return formatSessionTimeRemaining(getJwtSecondsUntilExpiry(refresh));
  });

  constructor() {
    if (!this.isBrowser) {
      return;
    }

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => {
        this.routeUrl.set(event.urlAfterRedirects);
        this.evaluate();
      });

    interval(30_000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.evaluate());

    const onFocus = () => this.evaluate();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        this.evaluate();
      }
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);

    this.destroyRef.onDestroy(() => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    });

    this.evaluate();
  }

  dismiss(): void {
    this.dismissed.set(true);
  }

  renew(): void {
    if (this.renewing()) {
      return;
    }

    this.renewing.set(true);
    this.auth.refreshAccessToken().subscribe({
      next: () => {
        this.renewing.set(false);
        this.dismissed.set(false);
        this.evaluate();
        this.notification.success('Sessão renovada com sucesso.');
      },
      error: () => {
        this.renewing.set(false);
      },
    });
  }

  private isPublicRoute(): boolean {
    const url = this.routeUrl();
    return url.startsWith('/login') || url.startsWith('/callback');
  }

  private getValidRefreshToken(): string | null {
    return this.auth.peekRefreshToken();
  }

  private evaluate(): void {
    this.tick.update((n) => n + 1);

    const refresh = this.getValidRefreshToken();
    if (!refresh) {
      return;
    }

    if (!this.auth.peekAccessToken() && !this.renewing()) {
      this.renewAccessSilently();
      return;
    }

    const remaining = getJwtSecondsUntilExpiry(refresh);
    if (remaining != null && remaining > SESSION_RENEWAL_WARNING_SEC) {
      this.dismissed.set(false);
    }
  }

  /** Renova o access_token ao voltar à aba, sem exigir navegação ou 401. */
  private renewAccessSilently(): void {
    this.renewing.set(true);
    this.auth.refreshAccessToken().subscribe({
      next: () => {
        this.renewing.set(false);
        this.dismissed.set(false);
        this.evaluate();
      },
      error: () => {
        this.renewing.set(false);
      },
    });
  }
}
